import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

const PlayerModal = ({ isOpen, onClose, video }) => {
  if (!isOpen || !video) return null;

  // Function to determine how to render the video based on available data
  const renderVideoContent = () => {
    // 1. If Admin provided raw HTML embed code, it takes priority
    if (video.embedHtml && video.embedHtml.trim() !== '') {
      return <div className="w-full h-full" dangerouslySetInnerHTML={{ __html: video.embedHtml }} />;
    }

    // 2. If Admin provided a simple URL, let's process it
    if (video.videoUrl && video.videoUrl.trim() !== '') {
      const url = video.videoUrl.trim();
      
      // Check for direct MP4 link
      if (url.toLowerCase().endsWith('.mp4') || url.toLowerCase().endsWith('.webm')) {
        return (
          <video 
            src={url} 
            controls 
            autoPlay 
            className="w-full h-full bg-black object-contain"
          />
        );
      }

      // Check for YouTube Link
      const ytMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?]+)/);
      if (ytMatch && ytMatch[1]) {
        return (
          <iframe 
            src={`https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1&rel=0`} 
            className="w-full h-full border-0" 
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
            allowFullScreen
            title={video.title}
          ></iframe>
        );
      }

      // Check for Vimeo Link
      const vimeoMatch = url.match(/vimeo\.com\/(?:video\/)?([0-9]+)/);
      if (vimeoMatch && vimeoMatch[1]) {
        return (
          <iframe 
            src={`https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1`} 
            className="w-full h-full border-0" 
            allow="autoplay; fullscreen; picture-in-picture" 
            allowFullScreen
            title={video.title}
          ></iframe>
        );
      }

      // Fallback: Just put the URL in an iframe and hope it allows embedding
      return (
        <iframe 
          src={url} 
          className="w-full h-full bg-black border-0" 
          allow="autoplay; fullscreen" 
          allowFullScreen
          title={video.title}
        ></iframe>
      );
    }

    // 3. Fallback if neither exists
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center text-muted">
        <SafeIcon name="VideoOff" className="w-16 h-16 mb-4 opacity-50" />
        <p>Playback source not available.</p>
        <p className="text-sm mt-2">Admin: Please set Video URL or Embed HTML for this video.</p>
      </div>
    );
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/95 backdrop-blur-md">
        <button 
          onClick={onClose} 
          className="absolute top-6 right-6 text-white/70 hover:text-white z-10 transition-colors"
        >
          <SafeIcon name="X" className="w-8 h-8" />
        </button>
        
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="w-full max-w-6xl p-4 md:p-8"
        >
          <div className="mb-6">
            <h2 className="text-2xl md:text-4xl font-bold text-white mb-2">{video.title}</h2>
            <p className="text-muted">{video.description}</p>
          </div>
          
          <div className="relative w-full aspect-video bg-dark-bg rounded-xl overflow-hidden shadow-2xl border border-dark-border">
            {renderVideoContent()}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default PlayerModal;